import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { apiClient, ApiError } from '@/utils/apiClient';
import { cancelTurnsForLogout } from '@/stores/appStore';

export interface AuthUser {
  id: number;
  username: string;
  email: string;
  role: string;
  email_verified?: boolean;
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;

  /** Set after registration when email verification is required. */
  pendingVerificationEmail: string | null;
}

interface AuthActions {
  login: (email: string, password: string) => Promise<void>;
  register: (username: string, email: string, password: string) => Promise<{ emailVerificationRequired: boolean }>;
  logout: () => void;
  loadProfile: (explicitToken?: string) => Promise<void>;
  verifyEmail: (token: string) => Promise<string>;
  resendVerification: (email: string) => Promise<string>;
  forgotPassword: (email: string) => Promise<string>;
  resetPassword: (token: string, newPassword: string) => Promise<string>;
  clearPendingVerification: () => void;
}

interface AuthStore extends AuthState, AuthActions {}

/**
 * Safe localStorage wrapper (mirrors pattern from appStore.ts).
 */
const safeLocalStorage: Storage = {
  getItem: (name: string) => {
    try {
      const value = localStorage.getItem(name);
      if (!value) return null;
      try {
        JSON.parse(value);
        return value;
      } catch {
        localStorage.removeItem(name);
        return null;
      }
    } catch {
      return null;
    }
  },
  setItem: (name: string, value: string) => {
    try {
      localStorage.setItem(name, value);
    } catch {
      // ignore
    }
  },
  removeItem: (name: string) => {
    try {
      localStorage.removeItem(name);
    } catch {
      // ignore
    }
  },
  clear: () => {
    try {
      localStorage.clear();
    } catch {
      // ignore
    }
  },
  key: (index: number) => {
    try {
      return localStorage.key(index);
    } catch {
      return null;
    }
  },
  get length() {
    try {
      return localStorage.length;
    } catch {
      return 0;
    }
  },
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      token: null,
      user: null,
      isAuthenticated: false,
      pendingVerificationEmail: null,

      login: async (email: string, password: string) => {
        try {
          const data = await apiClient.login(email, password);
          const token = data.access_token;
          apiClient.setAuthToken(token);

          set({
            token,
            user: {
              id: data.user_id,
              username: data.username,
              email: data.email,
              role: data.role,
            },
            isAuthenticated: true,
            pendingVerificationEmail: null,
          });

          // Fetch full profile in background
          try {
            const profile = await apiClient.getProfile();
            set({
              user: {
                id: profile.id,
                username: profile.username,
                email: profile.email,
                role: profile.role,
                email_verified: profile.email_verified,
              },
            });
          } catch {
            // profile fetch is best-effort; login already succeeded
          }
        } catch (err: any) {
          // Intercept the "email_not_verified" error and store the email
          // so the UI can offer a resend option.
          if (err?.message === 'email_not_verified') {
            set({ pendingVerificationEmail: email });
          }
          throw err;
        }
      },

      register: async (username: string, email: string, password: string) => {
        const data = await apiClient.register(username, email, password);

        if (data.email_verification_required) {
          set({ pendingVerificationEmail: email });
          return { emailVerificationRequired: true };
        }

        set({ pendingVerificationEmail: null });
        return { emailVerificationRequired: false };
      },

      logout: () => {
        // STORY-511: this tab's chat turns stop on the server too — sent
        // while the token is still set, which the request needs.
        cancelTurnsForLogout();
        apiClient.setAuthToken(null);
        set({
          token: null,
          user: null,
          isAuthenticated: false,
          pendingVerificationEmail: null,
        });
        // Notify appStore to clear user-scoped data
        window.dispatchEvent(new CustomEvent('auth-logout'));
      },

      loadProfile: async (explicitToken?: string) => {
        const token = explicitToken || get().token;
        if (!token) return;

        apiClient.setAuthToken(token);
        try {
          const profile = await apiClient.getProfile();
          set({
            token,
            user: {
              id: profile.id,
              username: profile.username,
              email: profile.email,
              role: profile.role,
              email_verified: profile.email_verified,
            },
            isAuthenticated: true,
          });
        } catch (err) {
          // Only a 401 means the token is actually invalid. Network errors
          // or 5xx (backend restarting during a reload) must not destroy
          // the session — the token may still be perfectly valid (STORY-197).
          if (err instanceof ApiError && err.status === 401) {
            set({ token: null, user: null, isAuthenticated: false });
            apiClient.setAuthToken(null);
          }
        }
      },

      verifyEmail: async (token: string) => {
        const res = await apiClient.verifyEmail(token);
        set({ pendingVerificationEmail: null });
        return res.message;
      },

      resendVerification: async (email: string) => {
        const res = await apiClient.resendVerification(email);
        return res.message;
      },

      forgotPassword: async (email: string) => {
        const res = await apiClient.forgotPassword(email);
        return res.message;
      },

      resetPassword: async (token: string, newPassword: string) => {
        const res = await apiClient.resetPassword(token, newPassword);
        return res.message;
      },

      clearPendingVerification: () => {
        set({ pendingVerificationEmail: null });
      },
    }),
    {
      name: 'mcp-auth-storage',
      storage: createJSONStorage(() => safeLocalStorage),
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      // Wire the persisted token into apiClient the moment hydration
      // completes — synchronously, before any component mounts. Without
      // this, panels that fetch on mount race App's useEffect: their
      // token-less requests 401, and those responses arriving after the
      // onUnauthorized hook is wired logged the user out on every
      // reload (STORY-197 / BUG-1.3.2_3).
      onRehydrateStorage: () => (state) => {
        apiClient.setAuthToken(state?.token ?? null);
      },
    }
  )
);
